import { isFramed } from '@wahala/vatar-client';
import { type FormEvent, type ReactNode, useEffect, useState } from 'react';
import { enterPark, errorText } from '../App';
import { type ApiConfig, ApiError, api, type ParkPreset, saveSession, type Theme } from '../lib/api';
import { useChatUnread } from '../lib/chatDot';
import { CODE_RE_CLIENT } from '../lib/code';
import { saveGuestKey } from '../lib/guest';
import { useInstallWay } from '../lib/install';
import { parkNaira } from '../lib/money';
import { preloadRoad } from '../lib/preload';
import { forgetPush } from '../lib/push';
import { sfx } from '../lib/sfx';
import { playHorn } from '../lib/sound';
import { preloadSolo, startSolo } from '../practice/solo';
import { useUi } from '../store/ui';
import { S } from '../strings';
import { SaveOffer } from '../ui/AccountDoor';
import { GetCoins } from '../ui/GetCoins';
import { Icon } from '../ui/icons';
import { Logo } from '../ui/Logo';
import { OpenParks } from '../ui/OpenParks';
import { PlaySettings } from '../ui/PlaySettings';
import { PushSettings } from '../ui/PushSettings';
import type { ParkLoadInfo } from '../ui/parkLoad';
import { SoundToggle, VolumeRow } from '../ui/SoundToggle';
import { StakeBoard } from '../ui/StakeBoard';
import { Html, W } from '../ui/wahala';

/**
 * A standalone link /join/{CODE} (spec FE-38, T-52) fills in the join box. Read once, then the
 * address goes back to / so a reload doesn't fill it again. The Lagos Life frame always loads /.
 */
function codeFromAddress(): string {
  const m = /^\/join\/([A-Za-z2-9]{6})\/?$/.exec(location.pathname);
  if (!m) return '';
  history.replaceState(null, '', '/');
  const c = (m[1] as string).toUpperCase();
  return CODE_RE_CLIENT.test(c) ? c : '';
}

/** The game types on the Play menu, in order (the server's switches decide which show). */
const TYPE_ORDER = ['park', 'race', 'hide', 'powers'] as const;
type TypeId = (typeof TYPE_ORDER)[number];
type Tile = 'solo' | TypeId | 'stake' | 'cash';
/** What the player's keke wears (the header and the side picker draw it). */
type KekeLook = { load?: string | undefined; finish?: string | undefined; slogan?: string | undefined };
/** The header's line changes every few seconds. */
const LINE_MS = 6_000;
const WEATHERS = ['day', 'night', 'rain', 'harmattan'] as const;

/** The game types switched on (an older server's /config lists only its presets: all on, Solo too). */
function typesOn(config: ApiConfig | null): Array<{ id: TypeId; solo: boolean; stake: boolean }> {
  const listed =
    config?.gameTypes ?? (config?.presets ?? ['park']).map((id) => ({ id, on: true, solo: true, stake: false }));
  return TYPE_ORDER.flatMap((id) => {
    const t = listed.find((x) => x.id === id);
    return t?.on ? [{ id, solo: t.solo, stake: t.stake }] : [];
  });
}

export function Menu() {
  const me = useUi((s) => s.me);
  const config = useUi((s) => s.config);
  const set = useUi((s) => s.set);
  const [code, setCode] = useState(codeFromAddress);
  const [busy, setBusy] = useState(false);
  const coinCount = useUi((s) => s.coins);
  /** The park still holding this player's seat (they came back to the menu without leaving it). */
  const [held, setHeld] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  /** Who is calling this player to drive together (the newest), if anyone. */
  const [caller, setCaller] = useState<string | null>(null);
  /** The tile whose sheet is open. */
  const [tile, setTile] = useState<Tile | null>(null);
  /** A stake table that never filled: its fare is back and a free round is on offer (PF-07). */
  const stakeOffer = useUi((s) => s.stakeOffer);
  /** What the player's keke wears (the header waves it down). */
  const [look, setLook] = useState<KekeLook>({});
  // a sheet asked for from elsewhere (the cooldown board's Play solo)
  const menuTile = useUi((s) => s.menuTile);
  useEffect(() => {
    if (!menuTile) return;
    setTile(menuTile);
    set({ menuTile: null });
  }, [menuTile, set]);

  useEffect(() => {
    api
      .wallet()
      .then((w) => set({ coins: w.coins }))
      .catch(() => {});
    api
      .me()
      .then((m) => setHeld(m.park))
      .catch(() => {});
    api
      .invites()
      .then((r) => setCaller(r.invites[0]?.from ?? null))
      .catch(() => {});
    api
      .profile()
      .then((p) => {
        const worn = (slot: 'load' | 'finish' | 'slogan') => p.items.find((i) => i.id === p.equipped[slot])?.item;
        setLook({ load: worn('load'), finish: worn('finish'), slogan: worn('slogan') });
      })
      .catch(() => {});
  }, [set]);

  /** Ask for a seat; `known` is what the way-in screen can show meanwhile (the game type, the code). */
  async function go(f: () => Promise<Parameters<typeof enterPark>[0]>, known: ParkLoadInfo = {}) {
    setBusy(true);
    const info: ParkLoadInfo = { ...(me?.name ? { driver: me.name } : {}), look, ...known };
    set({ notice: null, joining: true, entering: info });
    try {
      enterPark(await f(), info);
    } catch (e) {
      set({ notice: errorText(e), joining: false });
      if (e instanceof ApiError && e.code === 'in_another_park' && typeof e.body.code === 'string')
        setHeld(e.body.code);
    } finally {
      setBusy(false);
    }
  }

  function join(e: FormEvent) {
    e.preventDefault();
    void go(() => api.joinPark(code), { code });
  }

  const types = typesOn(config);
  // stake tables need the Lagos Life frame and a server whose platform has them (PF-18)
  const stakesHere =
    isFramed() && config?.lagosLife === true && config.platforms?.stakes !== false && types.some((t) => t.stake);
  const tiles: Tile[] = [
    ...(types.some((t) => t.solo) ? (['solo'] as const) : []),
    ...types.map((t) => t.id),
    ...(stakesHere ? (['stake'] as const) : []),
    // COMPETE AND WIN CASH is for website players only, never inside Lagos Life (its agreement
    // forbids real-money prizes); until the server switches it on, the tile carries a Coming soon stamp
    ...(isFramed() && config?.lagosLife ? [] : (['cash'] as const)),
  ];
  const cashOn = config?.compete?.on === true && !isFramed();
  // the community chat: our own website's players, never inside a distributor's frame, while the
  // park office has it on
  const chatHere = config?.platforms?.standalone === true && config.chat?.on === true && !isFramed() && Boolean(me);
  const chatNew = useChatUnread(chatHere);

  return (
    <main className="screen menu" data-theme="day">
      {/* one bar: the lockup, the wallet (opens the shop) and Menu */}
      <header className="menu__bar">
        <Logo className="logo menu__logo" bar />
        <button
          type="button"
          className="menu__wallet"
          aria-label={S.menu.shop}
          onClick={() => set({ screen: 'shop', notice: null })}
        >
          <Html html={W().coinWallet({ coins: coinCount ?? 0, button: false })} />
        </button>
        {chatHere && (
          <button
            type="button"
            className="menu__chat"
            aria-label={chatNew > 0 ? S.menu.chatUnread(chatNew) : S.menu.chat}
            onClick={() => set({ screen: 'chat', notice: null })}
          >
            <Icon name="chat" size={22} />
            {chatNew > 0 && <span className="menu__chat-dot">{chatNew > 99 ? '99+' : chatNew}</span>}
          </button>
        )}
        <button
          type="button"
          className="wh-btn wh-btn--quiet wh-btn--sm"
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => setOpen(true)}
        >
          {S.menu.open}
        </button>
      </header>
      {/* the welcome (How to play is put away for now: the road guide teaches in the round) */}
      <div className="menu__hello">
        <p>{me ? S.menu.hello(me.name) : S.menu.helloAnon}</p>
      </div>

      {/* a website guest who has finished a round: keep your profile (PF-19), until Not now */}
      <SaveOffer className="menu__save" />

      {caller && (
        <section className="card menu__call">
          <p className="small">{S.menu.invited(caller)}</p>
          <button
            type="button"
            className="wh-btn wh-btn--carry wh-btn--sm"
            onClick={() => set({ screen: 'people', notice: null })}
          >
            {S.menu.seeInvite}
          </button>
        </section>
      )}

      {stakeOffer && (
        <StakeBoard
          className="menu__stake-offer"
          band={S.stakes.board.band}
          aside={S.stakes.game(stakeOffer.game, stakeOffer.game === 'race' ? 2 : 4)}
          note={{
            title: S.stakes.board.expired,
            line: S.stakes.board.expiredLine(W().naira(stakeOffer.stake)),
            tone: 'info',
          }}
          status={S.stakes.board.freeLine}
        >
          <div className="stake-board__row">
            <button
              type="button"
              className="wh-btn wh-btn--carry wh-btn--md wh-btn--block"
              onClick={() => {
                set({ stakeOffer: null });
                startSolo(stakeOffer.game, 'surprise');
              }}
            >
              {S.stakes.board.free}
            </button>
            <button
              type="button"
              className="wh-btn wh-btn--quiet wh-btn--md wh-btn--block"
              onClick={() => {
                set({ stakeOffer: null });
                setTile('stake');
              }}
            >
              {S.stakes.board.back}
            </button>
          </div>
        </StakeBoard>
      )}

      {held && (
        <section className="card menu__held">
          <p className="small">{S.play.held(held)}</p>
          <button
            type="button"
            className="wh-btn wh-btn--carry wh-btn--sm"
            disabled={busy}
            onClick={() => void go(() => api.joinPark(held), { code: held })}
          >
            {S.play.back}
          </button>
        </section>
      )}

      <PlayHeader look={look} />

      <h2 className="menu__ask">
        <span>{S.play.ask}</span>
      </h2>
      {/* the tiles: Play alone (solo), the game types the park office has on, the stake table */}
      <Html
        className="wh-gt-grid menu__tiles"
        html={tiles.map((t) => W().gameTypeTile({ type: t, soon: t === 'cash' && !cashOn })).join('')}
        onClick={(e) => {
          const t = (e.target as HTMLElement).closest<HTMLElement>('[data-type]')?.dataset.type as Tile | undefined;
          if (!t || busy || (t === 'cash' && !cashOn)) return;
          sfx.click();
          playHorn(undefined);
          // the competition is its own screen, not a sheet over the menu
          if (t === 'cash') {
            set({ screen: 'compete' });
            return;
          }
          setTile(t);
        }}
      />

      <form className="wh-board menu__join" onSubmit={join}>
        <div className="wh-board__band">
          <h2>{S.menu.joinTitle}</h2>
          <Icon name="key" size={20} />
        </div>
        {/* the code and its button on one line */}
        <div className="join-row">
          <label className="sr-only" htmlFor="park-code">
            {S.menu.codeLabel}
          </label>
          <input
            id="park-code"
            className="code-input"
            value={code}
            onChange={(e) =>
              setCode(
                e.target.value
                  .toUpperCase()
                  .replace(/[^A-Z0-9]/g, '')
                  .slice(0, 6),
              )
            }
            placeholder={S.menu.codePlaceholder}
            inputMode="text"
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
            enterKeyHint="go"
          />
          <button
            className="wh-btn wh-btn--carry wh-btn--sm"
            type="submit"
            aria-label={S.menu.join}
            disabled={busy || !CODE_RE_CLIENT.test(code)}
          >
            {S.menu.joinShort}
          </button>
        </div>
      </form>

      {/* coin packs: a compact strip; the shop has the rest */}
      <GetCoins />

      {tile === 'solo' && (
        <SoloSheet types={types.filter((t) => t.solo).map((t) => t.id)} look={look} onClose={() => setTile(null)} />
      )}
      {tile && tile !== 'solo' && tile !== 'stake' && tile !== 'cash' && (
        <TypeSheet type={tile} busy={busy} go={go} onClose={() => setTile(null)} />
      )}
      {tile === 'stake' && (
        <PlaySheet title={S.play.stake} onClose={() => setTile(null)}>
          <p className="play-sheet__about">{S.play.about.stake}</p>
          <OpenParks
            busy={busy}
            stakesOnly
            onJoin={(c) => void go(() => api.joinPark(c), { code: c })}
            onStake={(f) => void go(f, { type: 'stakes4p' })}
          />
        </PlaySheet>
      )}

      {open && (
        <MenuSheet
          held={held}
          coinCount={coinCount}
          busy={busy}
          onClose={() => setOpen(false)}
          onBack={(code) => {
            setOpen(false);
            void go(() => api.joinPark(code), { code });
          }}
        />
      )}
    </main>
  );
}

/**
 * The street over the Play menu: the wordmark over a skyline in a random weather, an area boy
 * waving down your keke, his line changing every few seconds (still under reduced motion).
 */
function PlayHeader({ look }: { look: KekeLook }) {
  const [weather] = useState(() => WEATHERS[Math.floor(Math.random() * WEATHERS.length)] as (typeof WEATHERS)[number]);
  const [n, setN] = useState(0);
  useEffect(() => {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const t = setInterval(() => setN((x) => x + 1), LINE_MS);
    return () => clearInterval(t);
  }, []);
  const lines = W().areaBoyLines;
  return (
    <Html
      className="menu__header"
      html={W().playHeader({
        compact: true,
        weather,
        player: 'danfo',
        line: lines[n % lines.length] ?? '',
        ...(look.load ? { load: look.load as never } : {}),
        ...(look.finish ? { finish: look.finish as never } : {}),
        ...(look.slogan ? { slogan: look.slogan } : {}),
      })}
    />
  );
}

/** A sheet over the menu: a board with a title and Close; closes on the backdrop and Escape. */
function PlaySheet({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: Escape and the Close button cover the keyboard
    // biome-ignore lint/a11y/noStaticElementInteractions: the backdrop closes the sheet
    <div className="sheet" onClick={onClose}>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: only stops the backdrop's click */}
      <section
        className="sheet__panel wh-board sheet--board play-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="wh-board__band sheet__band">
          <h2>{title}</h2>
          <button type="button" className="wh-btn wh-btn--sm sheet__close" onClick={onClose}>
            {S.play.close}
          </button>
        </header>
        <div className="sheet__body play-sheet__body">{children}</div>
      </section>
    </div>
  );
}

/**
 * A game type's sheet: who you play with. Quick play (strangers, the park closest to starting) or
 * your own park with a code (let anyone join, a name and the weather for every round), then the
 * parks of this type waiting for people.
 */
function TypeSheet({
  type,
  busy,
  go,
  onClose,
}: {
  type: TypeId;
  busy: boolean;
  go: (f: () => Promise<Parameters<typeof enterPark>[0]>, known?: ParkLoadInfo) => Promise<void>;
  onClose: () => void;
}) {
  const config = useUi((s) => s.config);
  const [parkName, setParkName] = useState('');
  const [theme, setTheme] = useState<Theme | null>(null);
  /** Listed in the open parks, so strangers can find it. On unless the host turns it off. */
  const [listed, setListed] = useState(true);
  const prices = config?.perkPrices;
  const perkCost = (parkName.trim() && prices ? prices.name : 0) + (theme && prices ? prices.theme : 0);
  const preset = type as ParkPreset;
  return (
    <PlaySheet title={S.presets[type] ?? type} onClose={onClose}>
      {S.play.about[type] && <p className="play-sheet__about">{S.play.about[type]}</p>}
      <button
        type="button"
        className="wh-btn wh-btn--carry wh-btn--md wh-btn--block"
        disabled={busy}
        onClick={() => void go(() => api.quickPlay(preset), { type })}
      >
        {S.play.quick}
      </button>
      <p className="muted small center-text">{S.play.quickNote}</p>
      <section className="stack play-sheet__own">
        <div className="menu__opts">
          <label className="switch">
            <input type="checkbox" checked={listed} onChange={(e) => setListed(e.target.checked)} />
            <span>{S.menu.letAnyone}</span>
          </label>
          {prices && (
            <details className="perks">
              <summary>{S.perks.title}</summary>
              <p className="muted small">{S.perks.note}</p>
              <label className="field">
                <span>{S.perks.name(parkNaira(prices.name))}</span>
                <input
                  value={parkName}
                  maxLength={22}
                  onChange={(e) => setParkName(e.target.value)}
                  placeholder={S.perks.namePlaceholder}
                  autoComplete="off"
                />
              </label>
              <fieldset className="chips">
                <legend className="small">{S.perks.theme(parkNaira(prices.theme))}</legend>
                {/* night is not a weather to set: every round turns from day to evening to night */}
                {([null, 'day', 'rain', 'harmattan'] as const).map((t) => (
                  <button
                    key={t ?? 'random'}
                    type="button"
                    aria-pressed={theme === t}
                    className={`chip${theme === t ? ' is-on' : ''}`}
                    onClick={() => setTheme(t)}
                  >
                    {t ? S.lobby.theme[t] : S.perks.plain}
                  </button>
                ))}
              </fieldset>
              {perkCost > 0 && <p className="small">{S.perks.total(parkNaira(perkCost))}</p>}
            </details>
          )}
        </div>
        <button
          type="button"
          className="wh-btn wh-btn--md wh-btn--block"
          disabled={busy}
          onClick={() =>
            void go(
              () =>
                api.createPark(
                  preset,
                  {
                    ...(parkName.trim() ? { name: parkName.trim() } : {}),
                    ...(theme ? { theme } : {}),
                  },
                  listed,
                ),
              { type, ...(parkName.trim() ? { name: parkName.trim() } : {}) },
            )
          }
        >
          {S.play.open}
        </button>
        <p className="muted small center-text">{S.play.openNote}</p>
      </section>
      <OpenParks
        busy={busy}
        freeOnly
        preset={type}
        onJoin={(c) => void go(() => api.joinPark(c), { type, code: c })}
        onStake={() => {}}
      />
    </PlaySheet>
  );
}

/** The side Solo remembers between visits. */
const SIDE_KEY = 'wahala.solo.side';
type Side = 'driver' | 'agbero' | 'surprise';
function savedSide(): Side {
  try {
    const v = localStorage.getItem(SIDE_KEY);
    return v === 'driver' || v === 'agbero' || v === 'surprise' ? v : 'surprise';
  } catch {
    return 'surprise';
  }
}

/** Solo: pick a game, then your side (not in Race: it has none), then Start. No code, no lobby. */
function SoloSheet({ types, look, onClose }: { types: TypeId[]; look: KekeLook; onClose: () => void }) {
  const [type, setType] = useState<TypeId>(types[0] ?? 'park');
  const [side, setSide] = useState<Side>(savedSide);
  // Solo has no lobby to bake the street in: start on the picked game's map as soon as it is picked
  useEffect(() => {
    preloadRoad(type === 'race' ? 'third-mainland' : 'lagos-route');
    void preloadSolo(type).catch(() => {});
  }, [type]);
  const pickSide = (s: Side) => {
    setSide(s);
    try {
      localStorage.setItem(SIDE_KEY, s);
    } catch {
      // the pick is only remembered when the browser lets us
    }
  };
  const start = () => {
    onClose();
    startSolo(type, type === 'race' ? 'surprise' : side, look);
  };
  return (
    <PlaySheet title={S.play.solo} onClose={onClose}>
      <p className="solo-lead">{S.play.soloLead}</p>
      {/* the games as their menu tiles, smaller: the art and a line say what each is */}
      <section className="solo-pick" aria-label={S.play.soloWhat}>
        <h3 className="solo-pick__label">{S.play.soloWhat}</h3>
        <Html
          className="solo-games"
          html={types
            .map((t) =>
              W()
                .gameTypeTile({ type: t, className: type === t ? 'is-on' : '' })
                .replace('<button ', `<button aria-pressed="${type === t}" `),
            )
            .join('')}
          onClick={(e) => {
            const t = (e.target as HTMLElement).closest<HTMLElement>('[data-type]')?.dataset.type as TypeId | undefined;
            if (t && types.includes(t)) {
              sfx.click();
              setType(t);
            }
          }}
        />
      </section>
      {type === 'race' ? (
        // Race has no side: the player's own keke holds the side pick's place, so the sheet keeps its size
        <section className="wh-board solo-race" aria-label={S.play.soloRaceTitle}>
          <header className="wh-board__band">
            <h2>{S.play.soloRaceTitle}</h2>
          </header>
          <Html
            className="solo-race__keke"
            html={W().keke({
              player: 'danfo',
              size: 132,
              ...(look.load ? { load: look.load as never } : {}),
              ...(look.finish ? { finish: look.finish as never } : {}),
              ...(look.slogan ? { slogan: look.slogan } : {}),
            })}
          />
          <p className="solo-race__line">{S.play.soloRace}</p>
          <footer className="solo-race__foot">
            <button type="button" className="wh-btn wh-btn--carry wh-btn--lg wh-btn--block" onClick={start}>
              {S.play.soloStart}
            </button>
          </footer>
        </section>
      ) : (
        <Html
          className="solo-side"
          html={W().sidePicker({
            selected: side,
            player: 'danfo',
            hunt: type === 'hide',
            start: S.play.soloStart,
            ...(look.load ? { load: look.load as never } : {}),
            ...(look.finish ? { finish: look.finish as never } : {}),
          })}
          onClick={(e) => {
            const t = e.target as HTMLElement;
            const s = t.closest<HTMLElement>('[data-side]')?.dataset.side as Side | undefined;
            if (s) pickSide(s);
            else if (t.closest('[data-start]')) start();
          }}
        />
      )}
    </PlaySheet>
  );
}

/**
 * Everything that isn't opening or joining a park, in one list: tap Menu, pick where to go. Closes
 * on the backdrop, on Close and on Escape.
 */
function MenuSheet({
  held,
  coinCount,
  busy,
  onClose,
  onBack,
}: {
  held: string | null;
  coinCount: number | null;
  busy: boolean;
  onClose: () => void;
  onBack: (code: string) => void;
}) {
  const config = useUi((s) => s.config);
  const set = useUi((s) => s.set);
  // Get the app: everywhere but the Lagos Life frame and an installed app (lib/install)
  const installWay = useInstallWay();
  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);
  const go = (screen: 'shop' | 'pass' | 'today' | 'board' | 'history' | 'profile' | 'people') =>
    set({ screen, notice: null });

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: Escape and the Close button cover the keyboard
    // biome-ignore lint/a11y/noStaticElementInteractions: the backdrop closes the sheet
    <div className="sheet" onClick={onClose}>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: only stops the backdrop's click */}
      <nav
        className="sheet__panel wh-board sheet--board"
        role="dialog"
        aria-modal="true"
        aria-label={S.menu.title}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="wh-board__band sheet__band">
          <h2>{S.menu.title}</h2>
          <button type="button" className="wh-btn wh-btn--sm sheet__close" onClick={onClose}>
            {S.menu.close}
          </button>
        </header>
        <ul className="sheet__list sheet__body">
          {held && (
            <li>
              <button type="button" className="sheet__item is-first" disabled={busy} onClick={() => onBack(held)}>
                <span className="sheet__icon" data-tone="park">
                  <Icon name="park" />
                </span>
                <span className="sheet__text">
                  <span>{S.menu.backToPark}</span>
                  <small>{S.menu.held(held)}</small>
                </span>
                <Icon name="chevron" size={18} className="sheet__chev" />
              </button>
            </li>
          )}
          <li>
            <button type="button" className="sheet__item" onClick={() => go('profile')}>
              <span className="sheet__icon" data-tone="person">
                <Icon name="person" />
              </span>
              <span className="sheet__text">
                <span>{S.menu.profile}</span>
              </span>
              <Icon name="chevron" size={18} className="sheet__chev" />
            </button>
          </li>
          <li>
            <button type="button" className="sheet__item" onClick={() => go('people')}>
              <span className="sheet__icon" data-tone="people">
                <Icon name="people" />
              </span>
              <span className="sheet__text">
                <span>{S.menu.people}</span>
              </span>
              <Icon name="chevron" size={18} className="sheet__chev" />
            </button>
          </li>
          <li>
            <button type="button" className="sheet__item" onClick={() => go('shop')}>
              <span className="sheet__icon" data-tone="bag">
                <Icon name="bag" />
              </span>
              <span className="sheet__text">
                <span>{S.menu.shop}</span>
                <small>{S.menu.shopNote(parkNaira(coinCount ?? 0))}</small>
              </span>
              <Icon name="chevron" size={18} className="sheet__chev" />
            </button>
          </li>
          <li>
            <button type="button" className="sheet__item" onClick={() => go('pass')}>
              <span className="sheet__icon" data-tone="ticket">
                <Icon name="ticket" />
              </span>
              <span className="sheet__text">
                <span>{S.menu.pass}</span>
              </span>
              <Icon name="chevron" size={18} className="sheet__chev" />
            </button>
          </li>
          <li>
            <button type="button" className="sheet__item" onClick={() => go('today')}>
              <span className="sheet__icon" data-tone="flag">
                <Icon name="flag" />
              </span>
              <span className="sheet__text">
                <span>{S.menu.today}</span>
              </span>
              <Icon name="chevron" size={18} className="sheet__chev" />
            </button>
          </li>
          <li>
            <button type="button" className="sheet__item" onClick={() => go('board')}>
              <span className="sheet__icon" data-tone="trophy">
                <Icon name="trophy" />
              </span>
              <span className="sheet__text">
                <span>{S.menu.board}</span>
              </span>
              <Icon name="chevron" size={18} className="sheet__chev" />
            </button>
          </li>
          <li>
            <button type="button" className="sheet__item" onClick={() => go('history')}>
              <span className="sheet__icon" data-tone="clock">
                <Icon name="clock" />
              </span>
              <span className="sheet__text">
                <span>{S.menu.history}</span>
              </span>
              <Icon name="chevron" size={18} className="sheet__chev" />
            </button>
          </li>
          {installWay && (
            <li>
              <button
                type="button"
                className="sheet__item"
                onClick={() => {
                  onClose();
                  set({ installSheet: true });
                }}
              >
                <span className="sheet__icon" data-tone="phone">
                  <Icon name="phone" />
                </span>
                <span className="sheet__text">
                  <span>{S.install.get}</span>
                  <small>{S.install.getNote}</small>
                </span>
                <Icon name="chevron" size={18} className="sheet__chev" />
              </button>
            </li>
          )}
          <li className="sheet__section">
            <Icon name="gear" size={16} />
            {S.menu.settings}
          </li>
          <li className="sheet__row">
            <span>{S.menu.sound}</span>
            <SoundToggle />
          </li>
          <VolumeRow />
          <PlaySettings />
          <PushSettings />
          {/* visitors outside Lagos Life log out to come back under another name; inside it the name is
              always the player's Lagos Life name, so there is nothing to log out of */}
          {!(isFramed() && config?.lagosLife) && (
            <li>
              <button
                type="button"
                className="sheet__item"
                onClick={async () => {
                  useUi.getState().park?.leave();
                  // this phone's notifications go with the session (FE-51)
                  await forgetPush();
                  saveSession(null);
                  saveGuestKey(null);
                  set({ me: null, park: null, practice: null, screen: 'login' });
                }}
              >
                <span className="sheet__icon" data-tone="out">
                  <Icon name="out" />
                </span>
                <span className="sheet__text">
                  <span>{S.menu.logOut}</span>
                  <small>{S.menu.logOutNote}</small>
                </span>
              </button>
            </li>
          )}
        </ul>
      </nav>
    </div>
  );
}
